// Stable per-device identifier (issue #283).
//
// The backend scopes verification state per device, keyed by the value sent in
// the X-Device-ID header. This lives in its own module (rather than in api.ts)
// so both `apiCall` and the auth services' direct `/api/users/me` fetch can
// attach the header without importing api.ts — which would create the cycle
// api.ts → logic.ts → *AuthService → api.ts that forces those fetches to bypass
// apiCall in the first place. It depends only on StorageService.

import { StorageService } from '@/services/storage';

/** Request header carrying the stable per-device id. */
export const DEVICE_ID_HEADER = 'X-Device-ID';

// Cached in-module after the first read; persisted via StorageService so the id
// is stable across reloads.
let cachedDeviceId: string | undefined;

/**
 * Returns this device's stable id, generating and persisting one on first use.
 */
export async function ensureDeviceId(): Promise<string> {
  if (cachedDeviceId) return cachedDeviceId;
  const stored = await StorageService.getDeviceId();
  if (stored) {
    cachedDeviceId = stored;
    return stored;
  }
  const newId = crypto.randomUUID();
  await StorageService.setDeviceId(newId);
  cachedDeviceId = newId;
  return newId;
}
